import { Dialog, DialogProps } from "@mui/material";
import React, { SyntheticEvent, useRef } from "react";

/**
 * The ClickAwayDialog wraps the MUI Dialog component.  This component can be directly swapped in 
 * place of an existing Dialog.  It provides a more explicit click-to-close functionality that 
 * requires the user to click down outside AND click up outside of the Dialog in order to close it.
 * This fixes issues such as a user clicking and dragging over text in an input and accidentally 
 * going outside of the Dialog bounds when letting go, causing the Dialog to unexpectedly close.
 */

interface ClickawayDialogProps extends DialogProps {
  disableClickaway?: boolean;
}

export default function ClickawayDialog(props: ClickawayDialogProps): React.JSX.Element {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const mouseDownOutside = useRef<boolean>(false);
  const mouseUpOutside = useRef<boolean>(false);

  const { disableClickaway, ...nativeProps } = props;

  function handleMouseDown(event: SyntheticEvent) {
    mouseDownOutside.current = !boxRef.current?.contains(event.target as Node) ?? false;
  }

  function handleMouseUp(event: SyntheticEvent) {
    mouseUpOutside.current = !boxRef.current?.contains(event.target as Node) ?? false;
  }

  function handleOnClose(event: SyntheticEvent, reason: "backdropClick" | "escapeKeyDown") {
    // These prevent one dialog's close from closing another dialog further up the chain.
    event.preventDefault();
    event.stopPropagation();

    if (reason === "backdropClick" && disableClickaway) {
      return;
    }

    if ((mouseDownOutside.current && mouseUpOutside.current) || reason === "escapeKeyDown") {
      return props.onClose?.(event, reason);
    }
  }

  return (
    <Dialog
      {...nativeProps}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onClose={handleOnClose}
      slotProps={{
        paper: {
          ref: boxRef
        }
      }}>
    </Dialog>
  );
}